import { PayloadAction } from '@reduxjs/toolkit';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';

import { PleaseWait } from 'src/components/PleaseWait';
import { MedicareForm, MedicareFormRef } from 'src/components/forms/MedicareForm';
import { PHIFormRef, UpdatePHIForm } from 'src/components/forms/UpdatePHIForm';
import { ScrollablePanelBody, ScrollablePanelFooter, ScrollablePanelHeader } from 'src/components/scrollablePanel';
import { ScrollablePanel } from 'src/components/scrollablePanel/ScrollablePanel';
import { usePatientHasDetails } from 'src/hooks/useAsyncSelector';
import { MedicareCardStatus, PatientHasDetails, PatientHealthInfo, patientService } from 'src/services/patient';
import { useDispatch, useSelector } from 'src/store';
import { fetchPatientHasDetails } from 'src/store/bookingFlow';

export type Props = {
    isOpen?: boolean;
    isNewPatient: boolean;
    onContinue(medicareStatus: MedicareCardStatus, hasOshc: boolean): void;
    onCancel(): void;
    firstShown?: boolean;
    myMedicareRegisterPath?: string;
};

function assert<T>(truthy: T | undefined, errorMessage?: string): truthy is T {
    if (!truthy) {
        throw errorMessage || 'Assertion failed';
    }
    return !!truthy;
}

export function PreBookingInformation(props: Props) {
    return (
        <ScrollablePanel isOpen={props.isOpen} onClose={props.onCancel}>
            <InnerPreBookingInformation {...props} />
        </ScrollablePanel>
    );
}

function InnerPreBookingInformation({ isNewPatient, firstShown, onCancel, onContinue, myMedicareRegisterPath }: Props) {
    const medicareFormRef = useRef<MedicareFormRef>(null);
    const phiFormRef = useRef<PHIFormRef>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const siteId = useSelector((state) => state.appointment.clinicId);

    const {
        data: currentHealthInfo,
        isLoading: loadingHealthInfo,
        invoke: reloadHealthInfo,
    } = useAsyncData(
        {
            method: patientService.GetPatientHealthInfo,
            service: patientService,
        },
        siteId
    );

    const dispatch = useDispatch();
    const {
        data: patientHasDetails,
        error: patientHasDetailsError,
        isLoading: loadingPatientHasDetails,
    } = usePatientHasDetails();
    const reloadDetails = useCallback(
        async () => (await dispatch(fetchPatientHasDetails(siteId))) as PayloadAction<PatientHasDetails>,
        [dispatch, siteId]
    );

    const [initalValues, setInitialValues] = useState<{
        patientHasDetails: PatientHasDetails;
        currentHealthInfo: PatientHealthInfo;
    }>();
    useEffect(() => {
        if (patientHasDetails && currentHealthInfo) {
            setInitialValues({ patientHasDetails, currentHealthInfo });
        }
    }, [currentHealthInfo, patientHasDetails]);

    const onNext = useCallback(async () => {
        setIsSubmitting(true);
        if (!patientHasDetails) {
            throw 'Loading, please try again';
        }
        try {
            const [medicareForm, phiForm] = await Promise.all([
                medicareFormRef.current?.submit(),
                phiFormRef.current?.submit(),
            ]);
            let medicareStatus = patientHasDetails.medicareDetails.status;
            if (!assert(medicareForm) || !assert(phiForm)) {
                return;
            }
            if (medicareForm.haveMedicare === 'yes' && medicareForm.number) {
                await patientService.UpdatePatientBasicDetails(siteId, { medicare: medicareForm });
                medicareStatus = (await reloadDetails()).payload.medicareDetails.status;
            }
            const wantsPhi = phiForm.havePhi === 'oshc' || phiForm.havePhi === 'phi';
            const hadExistingPhi = !!currentHealthInfo.health_insurance_no;
            if (wantsPhi) {
                const hasChanged =
                    currentHealthInfo.health_insurance_no !== phiForm.customerId ||
                    (currentHealthInfo.health_insurance_expiry_date !== phiForm.expiry &&
                        !(currentHealthInfo.health_insurance_expiry_date === '2099-12-12' && phiForm.expiry === '')) ||
                    currentHealthInfo.health_insurance_name !== phiForm.providerName;
                if (hasChanged) {
                    await patientService.UpdatePatientHealthInfo(siteId, {
                        ExpiryDate: phiForm.expiry,
                        Name: phiForm.providerName,
                        Number: phiForm.customerId,
                    });
                }
            } else if (hadExistingPhi) {
                // Patient had PHI on file and has now said "No" - clear it.
                await patientService.UpdatePatientHealthInfo(siteId, {
                    ExpiryDate: '',
                    Name: '',
                    Number: '',
                });
            }
            reloadHealthInfo();
            onContinue(medicareStatus, phiForm.havePhi === 'oshc');
        } finally {
            setIsSubmitting(false);
        }
    }, [currentHealthInfo, onContinue, patientHasDetails, siteId, reloadHealthInfo, reloadDetails]);

    return !initalValues ? (
        <PleaseWait error={patientHasDetailsError} />
    ) : (
        <>
            <ScrollablePanelHeader>
                {firstShown ? (
                    isNewPatient ? (
                        <div className="text-sm font-normal text-gray-600">
                            <h2 className="mb-2 text-3xl font-semibold text-gray-900">
                                Do you have a medicare or health insurance card?
                            </h2>
                            <p>Please enter your Medicare or private health insurance details below.</p>
                        </div>
                    ) : (
                        <div className="text-sm font-normal text-gray-600">
                            <h2 className="mb-2 text-lg font-semibold text-gray-900 md:text-3xl">
                                Please check if your details are correct
                            </h2>
                            <p>
                                Please ensure your details are accurate. This helps us provide the best care and make
                                your visit as smooth as possible.
                            </p>
                        </div>
                    )
                ) : (
                    <h2 className="text-3xl font-semibold text-gray-900">Edit Details</h2>
                )}
            </ScrollablePanelHeader>
            <ScrollablePanelBody className="space-y-4 py-4">
                <>
                    <section className="px-6">
                        <MedicareForm
                            ref={medicareFormRef}
                            patientDetails={initalValues.patientHasDetails}
                            myMedicareRegisterPath={myMedicareRegisterPath}
                            required={isNewPatient}
                            prepend={
                                <>
                                    <h2 className="mb-1 text-base font-semibold">Medicare</h2>
                                    <div className="prose prose-sm">
                                        <p>
                                            Providing your Medicare details helps us process prescriptions efficiently.
                                            It also ensures you can access cheaper medications under the Pharmaceutical
                                            Benefits Scheme (PBS) and reduces any out-of-pocket costs where applicable.
                                        </p>
                                    </div>
                                </>
                            }
                            shouldShowValidation
                        />
                    </section>
                    <hr />
                    <section className="px-6">
                        <h2 className="mb-1 mt-5 text-base font-semibold">Health Insurance</h2>
                        <div className="prose prose-sm mb-4">
                            <p>
                                Your Medical Practice may partner with Health Insurance Companies to augment your care,
                                giving you access to free or discounted services.
                            </p>
                            <p>
                                All information is held in line with our{' '}
                                <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/privacy-policy`}>
                                    Privacy Policy
                                </a>
                                .
                            </p>
                        </div>
                        <UpdatePHIForm
                            ref={phiFormRef}
                            patientHealthInfo={initalValues.currentHealthInfo}
                            required={isNewPatient}
                        />
                    </section>
                </>
            </ScrollablePanelBody>
            <ScrollablePanelFooter>
                {({ didScrollBottom }) => (
                    <div
                        className={classNames('flex flex-row gap-3 sm:justify-end', {
                            'justify-end': !firstShown,
                            '*:w-full': firstShown,
                        })}
                    >
                        {!firstShown && onCancel && (
                            <Button type="button" color="gray" onClick={onCancel}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            className="text-center"
                            disabled={!didScrollBottom || loadingPatientHasDetails || loadingHealthInfo || isSubmitting}
                            onClick={onNext}
                            data-testid="submit-button"
                        >
                            {firstShown ? 'Continue' : 'Save Changes'}
                        </Button>
                    </div>
                )}
            </ScrollablePanelFooter>
        </>
    );
}
